
import { supabase } from "@/integrations/supabase/client";
import { toast } from 'sonner';

/**
 * Media types allowed for upload
 */
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

/**
 * Maximum file size in bytes (5MB)
 */
export const MAX_FILE_SIZE = 5 * 1024 * 1024;

/**
 * Image upload locations
 */
export enum ImageLocation {
  ARTICLE_CONTENT = 'article-content',
  ARTICLE_FEATURED = 'article-images',
}

/**
 * Interface for uploaded image metadata
 */
export interface UploadedImage {
  publicUrl: string;
  fileName: string;
  path: string;
  size: number;
}

/**
 * Options for image upload
 */
export interface ImageUploadOptions {
  location: ImageLocation;
  cacheControl?: string;
  showToast?: boolean;
}

/**
 * Service for handling all media operations
 */
export const mediaService = {
  /**
   * Uploads an image file to Supabase storage
   */
  async uploadImage(
    file: File, 
    options: ImageUploadOptions = { 
      location: ImageLocation.ARTICLE_CONTENT,
      cacheControl: '3600',
      showToast: true
    }
  ): Promise<UploadedImage | null> {
    try {
      // Validate file type
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        throw new Error(`Unsupported file type. Allowed types: ${ALLOWED_IMAGE_TYPES.join(', ')}`);
      }
      
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        throw new Error(`File size exceeds the ${MAX_FILE_SIZE / (1024 * 1024)}MB limit`);
      }

      // Generate unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${options.location}/${fileName}`;
      
      // Upload to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('articles')
        .upload(filePath, file, {
          cacheControl: options.cacheControl,
          upsert: false
        });
      
      if (uploadError) throw uploadError;
      
      // Get public URL for the uploaded file
      const { data: urlData } = supabase.storage
        .from('articles')
        .getPublicUrl(filePath);
      
      if (options.showToast) {
        toast.success('Image uploaded successfully');
      }
      
      return {
        publicUrl: urlData.publicUrl,
        fileName,
        path: filePath,
        size: file.size
      };
    } catch (error: any) {
      if (options.showToast) {
        toast.error(`Failed to upload image: ${error.message || 'Unknown error'}`);
      }
      console.error('Image upload error:', error);
      return null;
    }
  },

  /**
   * Fetches all images from a specific location
   */
  async fetchImages(location: ImageLocation): Promise<UploadedImage[]> {
    try {
      const { data, error } = await supabase.storage
        .from('articles')
        .list(location);
      
      if (error) throw error;
      
      if (!data) return [];
      
      // Filter for image files and map to public URLs
      return data
        .filter(file => file.name.match(/\.(jpeg|jpg|png|gif|webp)$/i))
        .map(file => {
          const { data: urlData } = supabase.storage
            .from('articles')
            .getPublicUrl(`${location}/${file.name}`);
            
          return {
            publicUrl: urlData.publicUrl,
            fileName: file.name,
            path: `${location}/${file.name}`,
            size: file.metadata?.size || 0
          };
        });
    } catch (error: any) {
      console.error('Error fetching images:', error);
      toast.error('Failed to load media library');
      return [];
    }
  },

  /**
   * Deletes an image from storage
   */
  async deleteImage(path: string): Promise<boolean> {
    try {
      const { error } = await supabase.storage
        .from('articles')
        .remove([path]);
      
      if (error) throw error;
      
      toast.success('Image deleted successfully');
      return true;
    } catch (error: any) {
      toast.error(`Failed to delete image: ${error.message || 'Unknown error'}`);
      return false;
    }
  }
};

